import React, { useEffect, useState } from 'react';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  updateDoc,
  doc,
  deleteDoc,
  increment,
} from 'firebase/firestore';
import firebase from '../db/firebase/firebase';
import { Trash2, Filter, Clock, Check, XCircle } from 'lucide-react';
import { motion } from 'framer-motion';

const RequestsProducts = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actions, setActions] = useState({});
  const [toast, setToast] = useState({ visible: false, message: '', type: 'success' });
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const q = query(collection(firebase.db, 'requestsProduct'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log('Requests data:', data);
      setRequests(data);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast({ visible: false, message: '', type: 'success' }), 3000);
  };

  const getStateStyle = (state) => {
    switch (state) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'approved': return 'bg-green-100 text-green-800 border-green-300';
      case 'rejected': return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this request?')) return;
    try {
      await deleteDoc(doc(firebase.db, 'requestsProduct', id));
      showToast('Request deleted');
    } catch (err) {
      console.error('Error deleting request:', err);
      showToast('Error deleting request', 'error');
    }
  };

  const handleApprove = async (id) => {
    if (!window.confirm('Are you sure you want to approve this request?')) return;
    const trackingNumber = actions[id]?.trackingNumber?.trim() || '';
    setActions(prev => ({ ...prev, [id]: { ...prev[id], loadingApprove: true } }));
    try {
      await updateDoc(doc(firebase.db, 'requestsProduct', id), {
        state: 'approved',
        ...(trackingNumber && { trackingNumber }),
      });
      setActions(prev => ({ ...prev, [id]: {} }));
      showToast('Request approved');
    } catch (err) {
      console.error('Error approving:', err);
      showToast('Failed to approve request', 'error');
    }
  };

  const handleReject = async (id) => {
    const reason = actions[id]?.rejectionReason?.trim();
    if (!reason) return showToast('Please enter a rejection reason', 'error');
    if (!window.confirm('Are you sure you want to reject this request? This action cannot be undone.')) return;
    setActions(prev => ({ ...prev, [id]: { ...prev[id], loadingReject: true } }));
    try {
      const request = requests.find(r => r.id === id);
      const userEmail = request?.email;
      const pointsUsed = request?.pointsUsed;

      await updateDoc(doc(firebase.db, 'requestsProduct', id), {
        state: 'rejected',
        rejectionReason: reason,
      });

      if (userEmail && typeof pointsUsed === 'number') {
        await updateDoc(doc(firebase.db, 'userData', userEmail), {
          points: increment(pointsUsed),
        });
      }

      setActions(prev => ({ ...prev, [id]: {} }));
      showToast('Request rejected and points returned');
    } catch (err) {
      console.error('Error rejecting:', err);
      showToast('Failed to reject request', 'error');
    }
  };

  const filteredRequests = [...requests]
    .filter(req => filter === 'all' || req.state === filter)
    .sort((a, b) => (a.state === 'pending' && b.state !== 'pending' ? -1 : 0));

  if (loading) {
    return (
      <div className="flex-1 h-full flex items-center justify-center bg-white z-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-gray-800"></div>
      </div>
    );
  }
  return (
    <div className="relative flex flex-col flex-1 h-screen bg-gray-100 p-4 overflow-hidden">
      {toast.visible && (
        <div className={`fixed top-5 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded shadow-lg z-50 text-white
          ${toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
          {toast.message}
        </div>
      )}

      <div className="flex justify-center gap-4 mb-4 flex-wrap">
        {[{ label: 'All', value: 'all', icon: <Filter size={18} /> },
          { label: 'Pending', value: 'pending', icon: <Clock size={18} /> },
          { label: 'Approved', value: 'approved', icon: <Check size={18} /> },
          { label: 'Rejected', value: 'rejected', icon: <XCircle size={18} /> }].map(({ label, value, icon }) => (
          <button
            key={value}
            onClick={() => setFilter(value)}
            className={`flex items-center gap-1 px-3 py-1 rounded-full border text-sm transition-all
              ${filter === value ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-800 border-gray-300 hover:bg-blue-100'}`}
          >
            {icon}
            {label}
          </button>
        ))}
      </div>

      <div className="overflow-y-auto flex-1 pr-2 pb-10">
        {filteredRequests.length === 0 ? (
          <div className="text-center text-gray-500 mt-20 text-lg">No requests found.</div>
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4">
            {filteredRequests.map((req) => (
              <motion.div
                key={req.id}
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
                className="break-inside-avoid bg-white rounded-2xl shadow p-4 border border-gray-200 hover:shadow-md transition-all duration-300 relative"
              >
                <button onClick={() => handleDelete(req.id)} className="absolute top-3 right-3 text-red-600 hover:text-red-800">
                  <Trash2 size={20} />
                </button>

                {req.imgUrl && (
                  <img
                    src={req.imgUrl}
                    alt={req.product}
                    className="w-full h-40 object-contain mb-4 rounded-xl"
                  />
                )}

                <h2 className="text-xl font-semibold text-gray-800 mb-2">{req.product}</h2>
                <p className="text-sm text-gray-600"><strong>Name:</strong> {req.name}</p>
                <p className="text-sm text-gray-600"><strong>Email:</strong> {req.email}</p>
                <p className="text-sm text-gray-600"><strong>Points Used:</strong> {req.pointsUsed}</p>
                <div className="mt-3 text-sm">
                  <p className="font-medium">Shipping Info:</p>
                  <p className="ml-2 text-gray-600">{req.shipping?.address}, {req.shipping?.city}</p>
                  <p className="ml-2 text-gray-600">{req.shipping?.postcode}, {req.shipping?.country}</p>
                </div>

                <div className={`mt-4 inline-block px-3 py-1 rounded-full text-xs font-semibold border ${getStateStyle(req.state)}`}>
                  {req.state.toUpperCase()}
                </div>
                <p className="mt-2 text-xs text-gray-400">Created: {new Date(req.createdAt).toLocaleString()}</p>

                {req.state === 'pending' && (
                  <div className="mt-4 space-y-3">
                    <input
                      type="text"
                      placeholder="Tracking number (optional)"
                      className="w-full border rounded px-3 py-2 text-sm"
                      value={actions[req.id]?.trackingNumber || ''}
                      onChange={(e) =>
                        setActions(prev => ({
                          ...prev,
                          [req.id]: { ...prev[req.id], trackingNumber: e.target.value }
                        }))
                      }
                    />
                    <button
                      onClick={() => handleApprove(req.id)}
                      disabled={actions[req.id]?.loadingApprove}
                      className="bg-green-600 text-white px-4 py-2 rounded w-full hover:bg-green-700 disabled:opacity-50"
                    >
                      {actions[req.id]?.loadingApprove ? 'Approving...' : 'Approve'}
                    </button>

                    <textarea
                      rows={3}
                      placeholder="Rejection reason"
                      className="w-full border rounded px-3 py-2 text-sm"
                      value={actions[req.id]?.rejectionReason || ''}
                      onChange={(e) =>
                        setActions(prev => ({
                          ...prev,
                          [req.id]: { ...prev[req.id], rejectionReason: e.target.value }
                        }))
                      }
                    />
                    <button
                      onClick={() => handleReject(req.id)}
                      disabled={actions[req.id]?.loadingReject}
                      className="bg-red-600 text-white px-4 py-2 rounded w-full hover:bg-red-700 disabled:opacity-50"
                    >
                      {actions[req.id]?.loadingReject ? 'Rejecting...' : 'Reject'}
                    </button>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RequestsProducts;